import { OverlayPortal as FloatingPortal } from "@/components/modal/OverlayPortal";
import { ButtonV2 } from "@/components/interactive/ButtonV2";
import { ZIndexClass } from "@/constants/z-index";
import { classNames } from "@/utils/classnames";
import { useLocalStorageSyncedState } from "@/utils/hooks/use-local-storage-synced-state";
import { LocalStorageKey } from "@/utils/local-storage-utils";
import { FloatingArrow, arrow, autoUpdate, offset, shift, useFloating } from "@floating-ui/react";
import { useCallback, useId, useRef } from "react";

interface NavExperimentCalloutState extends Record<string, unknown> {
  dismissedAt?: unknown;
}

const UNDISMISSED: NavExperimentCalloutState = {};

// FloatingArrow's default 7px tip, plus a 3px gap to the switch.
const ARROW_HEIGHT_PX = 7;
const CALLOUT_OFFSET_PX = ARROW_HEIGHT_PX + 3;

/** Whether this browser dismissed the navigation experiment's callout, and commands to change it. */
export const useNavExperimentCalloutDismissal = () => {
  const [state, setState] = useLocalStorageSyncedState(
    LocalStorageKey.NAV_EXPERIMENT_CALLOUT_DISMISSAL,
    UNDISMISSED,
  );
  const dismiss = useCallback(
    () => setState({ dismissedAt: new Date().toISOString() }),
    [setState],
  );
  const reset = useCallback(() => setState(null), [setState]);
  return { dismissed: typeof state.dismissedAt === "string", dismiss, reset };
};

/**
 * A card below the top rail's navigation switch that introduces the navigation experiment. It
 * stays until the reader dismisses it, which this browser then remembers. Portaled, as the
 * nudge cards are, so the top rail's overflow cannot clip it, and above the rail but below the
 * category rail's flyouts and modals.
 */
export const NavExperimentCallout: React.FC<{ anchor: HTMLElement | null }> = ({ anchor }) => {
  const { dismissed, dismiss } = useNavExperimentCalloutDismissal();
  const arrowRef = useRef<SVGSVGElement | null>(null);
  const id = useId();
  const open = anchor != null && !dismissed;
  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open,
    placement: "bottom",
    elements: { reference: anchor },
    middleware: [
      offset(CALLOUT_OFFSET_PX),
      shift({ padding: 8 }),
      arrow({ element: arrowRef, padding: 12 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  if (!open) return null;

  return (
    <FloatingPortal>
      <section
        ref={refs.setFloating}
        data-ds-v2=""
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-description`}
        // Hidden until placed, so it never paints at the corner it starts from.
        style={{ ...floatingStyles, visibility: isPositioned ? undefined : "hidden" }}
        className={classNames(
          "design-system-typography w-72 rounded-cds-6 border-[1px] p-cds-16",
          "border-cds-surface-border bg-cds-surface-default shadow-cds-m",
          "dark:border-white/[0.08] dark:bg-cds-elevated-dark dark:shadow-cds-l-dark",
          ZIndexClass.PRODUCT_NUDGE,
        )}
      >
        {/* Its first path is the outline, in the card's border color; the fill and its edge take
            the card's surface, so the arrow reads as part of the card. The outline is colored on
            the path rather than through currentColor, which dark-mode.css resets on every svg. */}
        <FloatingArrow
          ref={arrowRef}
          context={context}
          height={ARROW_HEIGHT_PX}
          strokeWidth={1}
          className={classNames(
            "fill-cds-surface-default stroke-cds-surface-default",
            "[&>path:first-of-type]:stroke-cds-surface-border",
            "dark:fill-cds-elevated-dark dark:stroke-cds-elevated-dark",
            "dark:[&>path:first-of-type]:stroke-white/[0.08]",
          )}
        />
        <h2
          id={`${id}-title`}
          className={"text-cds-md font-medium text-cds-ink-strong dark:text-zinc-100"}
        >
          Navigation experiment
        </h2>
        <p
          id={`${id}-description`}
          className={"mt-cds-6 text-cds-sm text-cds-ink-secondary dark:text-zinc-400"}
        >
          {"We're testing a customizable navigation. Try rearranging nav items and sections in " +
            "the left sidebar. Toggle back to the original nav above."}
        </p>
        <ButtonV2
          type={"button"}
          variant={"secondary"}
          size={"sm"}
          className={"mt-cds-16"}
          onClick={dismiss}
        >
          Got it
        </ButtonV2>
      </section>
    </FloatingPortal>
  );
};
